import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfirmVisit } from './mutation/use-confirm-visit';
import { useRejectVisit } from './mutation/use-reject-visit';

import { useDialog } from '@/hooks/use-dialog';

type VisitDialogAction = 'confirm' | 'reject';

interface VisitDialogPayload {
  visitId: number;
}

interface VisitDialogState {
  action: VisitDialogAction;
  payload: VisitDialogPayload;
}

export interface VisitActionDialogConfig {
  title: string;
  description: string;
  confirmButtonText: string;
  variant: 'info' | 'warning';
  isSubmitting: boolean;
  onConfirm: () => void;
}

export const useVisitActionDialog = () => {
  const { t } = useTranslation(['visit']);
  const [isOpen, openDialog, closeDialog] = useDialog();
  const [state, setState] = useState<VisitDialogState | null>(null);

  const handleCloseDialog = () => {
    closeDialog();
    setState(null);
  };

  const confirmVisit = useConfirmVisit();
  const rejectVisit = useRejectVisit();

  const openConfirmDialog = (payload: VisitDialogPayload) => {
    setState({ action: 'confirm', payload });
    openDialog();
  };

  const openRejectDialog = (payload: VisitDialogPayload) => {
    setState({ action: 'reject', payload });
    openDialog();
  };

  const getDialogConfig = () => {
    if (!state) {
      return null;
    }

    switch (state.action) {
      case 'confirm':
        return {
          title: t('visit:actionsDialog.confirm.title'),
          description: t('visit:actionsDialog.confirm.description', {
            visitId: state.payload.visitId,
          }),
          confirmButtonText: t('visit:actionsDialog.confirm.confirmButton'),
          variant: 'info' as const,
          isSubmitting: confirmVisit.isPending,
          onConfirm: () =>
            confirmVisit.mutate(state.payload.visitId, {
              onSuccess: handleCloseDialog,
            }),
        };
      case 'reject':
        return {
          title: t('visit:actionsDialog.reject.title'),
          description: t('visit:actionsDialog.reject.description', {
            visitId: state.payload.visitId,
          }),
          confirmButtonText: t('visit:actionsDialog.reject.confirmButton'),
          variant: 'warning' as const,
          isSubmitting: rejectVisit.isPending,
          onConfirm: () =>
            rejectVisit.mutate(state.payload.visitId, {
              onSuccess: handleCloseDialog,
            }),
        };
      default:
        return null;
    }
  };

  return {
    isOpen,
    openConfirmDialog,
    openRejectDialog,
    closeDialog: handleCloseDialog,
    dialogConfig: getDialogConfig(),
    isActionInProgress: confirmVisit.isPending || rejectVisit.isPending,
  };
};
